import React from 'react';
import { usePublicCompany } from '../hooks/usePublicCompany';
import LegalFooter from '../components/LegalFooter';

/**
 * Pagina pubblica con l'informativa privacy dell'app (artt. 13-14 GDPR), usata
 * anche come URL per App Store Connect. I dati del titolare arrivano da
 * /api/public/company (Impostazioni → Dati Agenzia).
 */

const UPDATED = '25 agosto 2026';

const Section: React.FC<{ n: string; title: string; children: React.ReactNode }> = ({ n, title, children }) => (
    <section className="mb-8">
        <h2 className="text-lg font-black text-slate-900 mb-3">
            <span className="text-rose-600">{n}.</span> {title}
        </h2>
        <div className="space-y-3 text-[14px] leading-relaxed text-slate-700">{children}</div>
    </section>
);

const PrivacyPolicyView: React.FC = () => {
    const company = usePublicCompany();
    const CONTACT_EMAIL = company.email;
    return (
        <div className="min-h-screen bg-slate-50 py-10 px-4">
            <div className="max-w-3xl mx-auto bg-white rounded-2xl shadow-sm border border-slate-200 p-8 sm:p-12">
                <header className="mb-8 pb-6 border-b border-slate-200">
                    <p className="text-[11px] font-black uppercase tracking-widest text-rose-600 mb-2">Ultra Detective</p>
                    <h1 className="text-2xl sm:text-3xl font-black text-slate-900">Informativa sulla Privacy</h1>
                    <p className="text-[13px] text-slate-500 mt-2">Ultimo aggiornamento: {UPDATED}</p>
                </header>

                <p className="text-[14px] leading-relaxed text-slate-700 mb-8">
                    La presente informativa descrive come vengono trattati i dati personali nell'ambito
                    dell'applicazione mobile e della piattaforma web <strong>Ultra Detective</strong> (di seguito
                    l'“App” e la “Piattaforma”), gestionale professionale destinato ad agenzie investigative e
                    di vigilanza privata autorizzate. Il trattamento avviene nel rispetto del Regolamento (UE)
                    2016/679 (“GDPR”) e del D.Lgs. 196/2003 come modificato dal D.Lgs. 101/2018.
                </p>

                <Section n="1" title="Titolare del trattamento">
                    <p>
                        Titolare del trattamento dei dati raccolti nell'ambito dell'attività professionale è
                        l'agenzia che utilizza il gestionale:
                    </p>
                    <p className="bg-slate-50 border border-slate-200 rounded-lg p-4 not-italic">
                        <strong>{company.agencyName}</strong><br />
                        {company.fullAddress && (<>{company.fullAddress}<br /></>)}
                        {company.vatNumber && (<>P.IVA / C.F. {company.vatNumber}<br /></>)}
                        {company.phone && (<>Tel: {company.phone}<br /></>)}
                        Email: <a className="text-rose-600 underline" href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>
                        {company.website && (<><br />Sito: {company.website}</>)}
                    </p>
                </Section>

                <Section n="2" title="Natura del servizio e ruoli (architettura multi-tenant)">
                    <p>
                        Ultra Detective è uno strumento di lavoro <strong>ad uso esclusivamente professionale</strong>:
                        l'accesso è riservato agli operatori (titolari, responsabili e collaboratori) di un'agenzia
                        che dispone di un proprio codice azienda. Ogni agenzia dispone di un'installazione dedicata
                        dei dati (modello “on-premise / multi-tenant”): i dati di un'agenzia non sono accessibili ad
                        altre agenzie.
                    </p>
                    <p>
                        Rispetto ai dati inseriti dall'operatore relativi alla propria attività investigativa — in
                        particolare i dati dei <strong>clienti</strong> e dei <strong>soggetti oggetto di indagine</strong> —
                        <strong> ciascuna agenzia agisce quale autonomo Titolare del trattamento</strong>, in quanto
                        determina finalità e mezzi della propria attività. Il fornitore del software opera, per tali
                        dati, quale <strong>Responsabile del trattamento</strong> ai sensi dell'art. 28 GDPR, nei
                        limiti dell'assistenza tecnica e sulla base di apposito accordo. L'operatore che inserisce dati
                        di terzi è tenuto a disporre di idonea base giuridica e a fornire agli interessati la relativa
                        informativa.
                    </p>
                </Section>

                <Section n="3" title="Categorie di dati trattati">
                    <ul className="list-disc pl-5 space-y-2">
                        <li>
                            <strong>Dati di account e profilo:</strong> nome, cognome, indirizzo email, numero di
                            telefono, ruolo, azienda di appartenenza (codice azienda), credenziali di accesso
                            (conservate in forma cifrata/hashed).
                        </li>
                        <li>
                            <strong>Dati di geolocalizzazione:</strong> coordinate GPS rilevate all'avvio e al termine
                            dei turni/servizi e degli incarichi, anche mentre l'App è in background durante un servizio
                            attivo, ed eventuale città ricavata dalle coordinate. Servono a documentare presenza e
                            orari dell'attività.
                        </li>
                        <li>
                            <strong>Foto e video:</strong> immagini e filmati acquisiti come prova degli incarichi e
                            foto operative dei turni (es. punto vendita, contachilometri), inclusi i relativi metadati
                            tecnici (EXIF: data/ora e, se presenti, coordinate di scatto).
                        </li>
                        <li>
                            <strong>Dati di attività:</strong> sessioni di lavoro (orari di inizio/fine), chilometri
                            percorsi, relazioni/report giornalieri (testo), note, spese e relativi giustificativi
                            (scontrini) con importi.
                        </li>
                        <li>
                            <strong>Dati di clienti e soggetti di indagine</strong> inseriti dall'operatore
                            professionale: possono includere dati identificativi e, a seconda dell'attività,
                            categorie particolari di dati. Sono trattati per conto e sotto la responsabilità
                            dell'agenzia (v. punto 2).
                        </li>
                        <li>
                            <strong>Token per le notifiche push</strong> associato al dispositivo, per il recapito
                            delle notifiche di servizio.
                        </li>
                        <li>
                            <strong>Dato biometrico (Face ID / impronta):</strong> se l'utente lo utilizza per lo
                            sblocco dell'App, l'elaborazione avviene <strong>esclusivamente sul dispositivo</strong>
                            tramite i sistemi del sistema operativo (Apple/Google). Il dato biometrico
                            <strong> non è mai trasmesso né conservato</strong> dai nostri sistemi.
                        </li>
                    </ul>
                    <p>
                        I log tecnici applicativi sono progettati per <strong>non contenere dati personali</strong>:
                        registrano identificativi numerici, azioni e orari, non email, nomi, recapiti o testi liberi.
                    </p>
                </Section>

                <Section n="4" title="Finalità e basi giuridiche">
                    <ul className="list-disc pl-5 space-y-2">
                        <li><strong>Erogazione del servizio</strong> (gestione incarichi/turni, prove, report, spese, notifiche): esecuzione del rapporto contrattuale/di lavoro (art. 6.1.b GDPR).</li>
                        <li><strong>Sicurezza e integrità</strong> (autenticazione, tracciabilità/audit delle azioni, prevenzione abusi): legittimo interesse (art. 6.1.f GDPR).</li>
                        <li><strong>Adempimenti di legge</strong> (obblighi fiscali e contabili): art. 6.1.c GDPR.</li>
                        <li><strong>Funzioni facoltative</strong> (sblocco biometrico, notifiche push, notifiche WhatsApp ove attivate): consenso dell'interessato o legittimo interesse, revocabile in qualsiasi momento dalle impostazioni.</li>
                    </ul>
                </Section>

                <Section n="5" title="Permessi del dispositivo">
                    <p>L'App richiede i seguenti permessi, utilizzati esclusivamente per le funzioni indicate:</p>
                    <ul className="list-disc pl-5 space-y-2">
                        <li><strong>Posizione</strong> (anche in background durante un servizio attivo): registrazione di inizio/fine turni e incarichi.</li>
                        <li><strong>Fotocamera:</strong> acquisizione delle foto/video richiesti da turni e incarichi.</li>
                        <li><strong>Libreria foto:</strong> allegare immagini/documenti esistenti e, se scelto, salvare le foto scattate.</li>
                        <li><strong>Face ID / biometria:</strong> protezione dell'accesso rapido ai dati riservati (elaborazione locale sul dispositivo).</li>
                        <li><strong>Notifiche:</strong> recapito degli avvisi di servizio.</li>
                        <li><strong>Esecuzione in background:</strong> completamento del caricamento delle prove anche ad app chiusa.</li>
                    </ul>
                    <p>Il microfono <strong>non</strong> è utilizzato per registrare audio: il relativo permesso è richiesto unicamente dalla libreria della fotocamera in caso di ripresa video.</p>
                </Section>

                <Section n="6" title="Nessun tracciamento pubblicitario">
                    <p>
                        L'App e la Piattaforma <strong>non integrano SDK di analytics, profilazione o pubblicità</strong>
                        e <strong>non tracciano</strong> l'utente attraverso app o siti di terzi. I dati sono trattati
                        unicamente per il funzionamento del servizio e non sono venduti né ceduti per finalità di
                        marketing.
                    </p>
                </Section>

                <Section n="7" title="Destinatari e terze parti (responsabili/fornitori)">
                    <p>Per erogare il servizio ci avvaliamo dei seguenti fornitori, che trattano i dati per nostro conto o quali autonomi titolari nei rispettivi ambiti:</p>
                    <ul className="list-disc pl-5 space-y-2">
                        <li><strong>Apple Inc.</strong> — distribuzione dell'App tramite App Store.</li>
                        <li><strong>Google / Firebase Cloud Messaging</strong> — invio delle notifiche push (token del dispositivo).</li>
                        <li><strong>Amazon Web Services / Backblaze</strong> — archiviazione cifrata dei file e delle prove (foto/video/documenti).</li>
                        <li><strong>OpenAI</strong> — <strong>funzione facoltativa</strong> di assistenza alla stesura delle relazioni (“Migliora relazione”), disponibile solo se l'agenzia la abilita configurando una propria chiave: in tal caso il solo testo inserito viene inviato al servizio per l'elaborazione. La funzione è disattivata di default.</li>
                        <li><strong>Cloudflare</strong> — protezione della connessione e controllo degli accessi (su alcune installazioni).</li>
                    </ul>
                    <p>
                        <strong>Trasferimenti extra-UE:</strong> alcuni fornitori (es. Apple, Google, OpenAI) possono
                        trattare dati anche al di fuori dello Spazio Economico Europeo. Tali trasferimenti avvengono
                        sulla base delle garanzie previste dagli artt. 44 ss. GDPR (in particolare le Clausole
                        Contrattuali Standard della Commissione Europea).
                    </p>
                </Section>

                <Section n="8" title="Conservazione dei dati">
                    <p>
                        I dati operativi (incarichi, turni, relazioni, spese, prove) sono conservati per il tempo
                        necessario alle finalità sopra indicate e per gli obblighi di legge. I dati con rilevanza
                        fiscale/contabile sono conservati per il termine di legge (di regola 10 anni).
                    </p>
                    <p>
                        Il gestionale mette a disposizione dell'agenzia meccanismi configurabili di
                        <strong> anonimizzazione</strong> e <strong>cancellazione</strong> dei dati (di norma
                        disattivati di default): l'agenzia, quale titolare dei dati di clienti e soggetti di indagine,
                        stabilisce i periodi di conservazione ed è tenuta a evadere le richieste degli interessati.
                        In caso di anonimizzazione, i dati identificativi vengono sovrascritti in modo irreversibile,
                        mantenendo i soli dati aggregati necessari agli obblighi contabili.
                    </p>
                </Section>

                <Section n="9" title="Sicurezza">
                    <p>Adottiamo misure tecniche e organizzative adeguate, tra cui:</p>
                    <ul className="list-disc pl-5 space-y-2">
                        <li>cifratura “at-rest” dei dati personali più sensibili (es. email, coordinate GPS e altri identificativi) e cifratura del canale di comunicazione (TLS/HTTPS);</li>
                        <li>autenticazione degli utenti e sblocco biometrico opzionale sul dispositivo;</li>
                        <li>registro delle azioni (audit log) privo di dati personali in chiaro;</li>
                        <li>segregazione dei dati per agenzia (multi-tenant) e accesso riservato ai soli utenti autorizzati.</li>
                    </ul>
                </Section>

                <Section n="10" title="Diritti dell'interessato">
                    <p>
                        Gli interessati possono esercitare in ogni momento i diritti di cui agli artt. 15-22 GDPR:
                        accesso, rettifica, cancellazione, limitazione, portabilità e opposizione al trattamento.
                        Le richieste relative ai dati di clienti/soggetti di indagine vanno indirizzate all'agenzia
                        titolare; per i dati di account e per l'App è possibile contattare il Titolare ai recapiti
                        indicati al punto 1. È inoltre possibile proporre reclamo al <strong>Garante per la protezione
                        dei dati personali</strong> (www.garanteprivacy.it).
                    </p>
                </Section>

                <Section n="11" title="Minori">
                    <p>Il servizio è rivolto esclusivamente a operatori professionali e <strong>non è destinato a minori di 18 anni</strong>. Non raccogliamo consapevolmente dati di minori.</p>
                </Section>

                <Section n="12" title="Modifiche all'informativa">
                    <p>La presente informativa può essere aggiornata nel tempo. La versione vigente è sempre pubblicata a questo indirizzo, con indicazione della data di ultimo aggiornamento.</p>
                </Section>

                <Section n="13" title="Contatti">
                    <p>Per qualsiasi richiesta relativa al trattamento dei dati: <a className="text-rose-600 underline" href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a> — {company.agencyName}{company.fullAddress ? `, ${company.fullAddress}` : ''}.</p>
                </Section>

                <footer className="mt-10 pt-6 border-t border-slate-200 text-[12px] text-slate-400">
                    {company.agencyName} — Informativa privacy — {UPDATED}
                </footer>

                <LegalFooter />
            </div>
        </div>
    );
};

export default PrivacyPolicyView;
